import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import paymentReminderService from '../../../services/paymentReminderService';

const OverdueInvoicesPanel = ({ companyId, company, onReminderSent }) => {
  const [overdueInvoices, setOverdueInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sendingReminder, setSendingReminder] = useState(null);
  const [sendingBulk, setSendingBulk] = useState(false);

  useEffect(() => {
    loadOverdueInvoices();
  }, [companyId]);

  const loadOverdueInvoices = async () => {
    try {
      setLoading(true);
      setError('');
      const invoices = await paymentReminderService?.getOverdueInvoices(companyId);
      setOverdueInvoices(invoices);
    } catch (err) {
      setError(err?.message || 'Failed to load overdue invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminder = async (invoice) => {
    try {
      setSendingReminder(invoice?.id);
      
      let reminderType = 'standard';
      if (invoice?.daysOverdue > 30) {
        reminderType = 'final';
      } else if (invoice?.daysOverdue > 14) {
        reminderType = 'urgent';
      }

      await paymentReminderService?.sendPaymentReminder(invoice, company, reminderType);
      
      // Reload invoices to update reminder metadata
      await loadOverdueInvoices();
      
      if (onReminderSent) {
        onReminderSent();
      }
      
      alert(`Payment reminder sent successfully for invoice ${invoice?.invoiceNumber}`);
    } catch (err) {
      alert(`Failed to send reminder: ${err?.message}`);
    } finally {
      setSendingReminder(null);
    }
  };

  const handleSendBulkReminders = async () => {
    if (!confirm(`Send payment reminders for all ${overdueInvoices?.length} overdue invoices?`)) {
      return;
    }

    try {
      setSendingBulk(true);
      const result = await paymentReminderService?.sendBulkReminders(companyId, company);
      
      await loadOverdueInvoices();
      
      if (onReminderSent) {
        onReminderSent();
      }
      
      alert(`Bulk reminders sent: ${result?.sent} successful, ${result?.failed} failed`);
    } catch (err) {
      alert(`Failed to send bulk reminders: ${err?.message}`);
    } finally {
      setSendingBulk(false);
    }
  };

  const getUrgencyColor = (daysOverdue) => {
    if (daysOverdue > 30) return 'text-error bg-error/10';
    if (daysOverdue > 14) return 'text-warning bg-warning/10';
    return 'text-warning bg-warning/10';
  };

  const getUrgencyLabel = (daysOverdue) => {
    if (daysOverdue > 30) return 'CRITICAL';
    if (daysOverdue > 14) return 'URGENT';
    return 'OVERDUE';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader" className="w-6 h-6 text-primary animate-spin" />
          <span className="ml-2 text-gray-600">Loading overdue invoices...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center py-8">
          <Icon name="AlertCircle" className="w-12 h-12 text-error mx-auto mb-3" />
          <p className="text-error">{error}</p>
          <button
            onClick={loadOverdueInvoices}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
            <Icon name="AlertTriangle" className="w-5 h-5 text-error" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Overdue Invoices</h3>
            <p className="text-sm text-gray-500">{overdueInvoices?.length || 0} invoices require payment</p>
          </div>
        </div>
        {overdueInvoices?.length > 0 && (
          <Button
            onClick={handleSendBulkReminders}
            disabled={sendingBulk}
            className="flex items-center gap-2"
          >
            {sendingBulk ? (
              <>
                <Icon name="Loader" className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Icon name="Send" className="w-4 h-4" />
                Send All Reminders
              </>
            )}
          </Button>
        )}
      </div>
      {/* Content */}
      <div className="p-6">
        {overdueInvoices?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="CheckCircle" className="w-16 h-16 text-success mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-gray-900 mb-2">All Caught Up!</h4>
            <p className="text-gray-600">No overdue invoices at this time.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {overdueInvoices?.map((invoice) => (
              <div
                key={invoice?.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-semibold text-gray-900">
                        Invoice #{invoice?.invoiceNumber}
                      </h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getUrgencyColor(invoice?.daysOverdue)}`}>
                        {getUrgencyLabel(invoice?.daysOverdue)} - {invoice?.daysOverdue} Days
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Amount Due:</span>
                        <div className="font-semibold text-gray-900">K{invoice?.amountDue?.toFixed(2)}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Due Date:</span>
                        <div className="font-semibold text-error">
                          {invoice?.dueDate ? new Date(invoice?.dueDate)?.toLocaleDateString('en-PG') : 'N/A'}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-500">Billing Period:</span>
                        <div className="font-semibold text-gray-900">
                          {invoice?.billingPeriodStart} - {invoice?.billingPeriodEnd}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-500">Last Reminder:</span>
                        <div className="font-semibold text-gray-900">
                          {invoice?.reminderSentAt
                            ? new Date(invoice?.reminderSentAt)?.toLocaleDateString('en-PG')
                            : 'Never'}
                        </div>
                      </div>
                    </div>
                    {invoice?.reminderCount > 0 && (
                      <div className="mt-2 text-xs text-gray-500">
                        <Icon name="Mail" className="w-3 h-3 inline mr-1" />
                        {invoice?.reminderCount} reminder(s) sent
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => handleSendReminder(invoice)}
                      disabled={sendingReminder === invoice?.id}
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                    >
                      {sendingReminder === invoice?.id ? (
                        <>
                          <Icon name="Loader" className="w-4 h-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Icon name="Send" className="w-4 h-4" />
                          Send Reminder
                        </>
                      )}
                    </button>
                    {invoice?.hostedInvoiceUrl && (
                      <a
                        href={invoice?.hostedInvoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="View invoice"
                      >
                        <Icon name="ExternalLink" className="w-5 h-5 text-gray-600" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OverdueInvoicesPanel;